import { Box, Button } from '@mui/material'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import {
    DeviceManagerSettingsFields,
    DeviceManagerSettingsType,
} from '../types'
import { flattenEntityTemplates } from '../../../../utils/EntityUtils'
import { Applications, AttributeType } from '../../../../constants/constants'
import Fields from '../../Components/Fields'
import { updateApplicationSettings } from '../../../../services/applications'
import { updateDeviceManagerSettings } from '../../../../redux/slices/deviceManagerSlice'

const deviceSettingsFields: DeviceManagerSettingsFields[] = [
    {
        id: 'enable',
        label: 'Enable Device Manager',
        dataType: AttributeType.BOOLEAN,
        required: true,
        value: false,
    },
    {
        id: 'deviceTemplateId',
        label: 'Device Template',
        dataType: AttributeType.SELECT,
        list: [],
        required: true,
        value: '',
    },
    {
        id: 'deviceDashboardId',
        label: 'Device Dashboard',
        dataType: AttributeType.TEXT,
        required: false,
        value: '',
    },
]

interface DeviceSettignsPropsTypes {
    hasWriteAccess: boolean
}

const DeviceSettings = (props: DeviceSettignsPropsTypes): ReactElement => {
    const { hasWriteAccess } = props
    const { t } = useTranslation()
    const navList = useAppSelector((state) => state.nav.navList)
    const org = useAppSelector((state) => state.org.org)
    const deviceManagerSettings = useAppSelector(
        (state) => state.deviceManager.deviceManagerSettings,
    )
    const orgId = org?.id ? org.id : ''

    const dispatch = useAppDispatch()

    const [settingsFields, setSettingsFields] =
        useState<DeviceManagerSettingsFields[]>(deviceSettingsFields)

    const onChangeFields = useCallback(
        (id: string, value: boolean | string | any[] | null, key?: string) => {
            setSettingsFields((prev) =>
                prev.map((item) => {
                    if (item.id === id) {
                        const obj = { ...item }
                        obj[key ?? 'value'] = value
                        obj['errorMessage'] = ''
                        return obj
                    } else {
                        return item
                    }
                }),
            )
        },
        [],
    )

    const handleSettingsReset = useCallback(() => {
        setSettingsFields((prev) => {
            return prev.map((item) => {
                return {
                    ...item,
                    value: deviceManagerSettings[item.id] || null,
                }
            })
        })
    }, [deviceManagerSettings])

    const handleFieldsValidation = useCallback(
        <T extends DeviceManagerSettingsFields>(
            fields: T[],
        ): { isValid: boolean; updatedFields: T[] } => {
            let isValid = true
            const updatedFields = fields?.map((item) => {
                const isEmpty =
                    item.value === null ||
                    item.value === undefined ||
                    item.value === '' ||
                    (Array.isArray(item.value) && item.value.length === 0)
                if (isEmpty && item.required) {
                    isValid = false
                    return { ...item, errorMessage: 'This field is required' }
                } else {
                    return item
                }
            })

            return { isValid, updatedFields }
        },
        [],
    )

    const handleSave = useCallback(async () => {
        const { isValid, updatedFields } =
            handleFieldsValidation(settingsFields)
        if (!isValid) {
            setSettingsFields(updatedFields)
            return
        }
        const settings: DeviceManagerSettingsType =
            {} as DeviceManagerSettingsType

        settingsFields.forEach((item) => {
            settings[item.id] = item.value
        })
        //updating redux store
        dispatch(updateDeviceManagerSettings(settings))
        //updating in DB
        await updateApplicationSettings<DeviceManagerSettingsType>(
            orgId,
            Applications.deviceManager.id,
            settings,
        )
    }, [handleFieldsValidation, settingsFields, dispatch, orgId])

    useEffect(() => {
        if (navList?.length) {
            const updatedEntityTypes = flattenEntityTemplates(navList)
            setSettingsFields((prev) =>
                prev.map((item) =>
                    item.id === 'deviceTemplateId'
                        ? { ...item, list: updatedEntityTypes }
                        : item,
                ),
            )
        }
    }, [navList])

    useEffect(() => {
        if (
            deviceManagerSettings &&
            Object.keys(deviceManagerSettings).length > 0
        ) {
            handleSettingsReset()
        }
    }, [deviceManagerSettings, handleSettingsReset])

    return (
        <Box
            sx={{
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    width: '500px',
                }}
            >
                <Fields
                    fields={settingsFields}
                    onChangeFields={onChangeFields}
                    disabled={!hasWriteAccess}
                />
            </Box>

            {hasWriteAccess && (
                <Box
                    sx={{
                        width: '500px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 2,
                    }}
                >
                    <Button
                        id="intransit-settings-button-reset"
                        variant="contained"
                        color="secondary"
                        onClick={handleSettingsReset}
                        sx={{
                            mr: 2,
                            border: '1px solid #3a5e98',
                            backgroundColor: 'white',
                            color: '#3a5e98',
                        }}
                    >
                        {t('reset')}
                    </Button>
                    <Button
                        id="intransit-settings-button-save"
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        sx={{
                            backgroundColor: '#3a5e98',
                            color: 'white',
                        }}
                    >
                        {t('save')}
                    </Button>
                </Box>
            )}
        </Box>
    )
}

export default DeviceSettings
